import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type ToastKind = 'success' | 'error' | 'info' | 'warn';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  durationMs: number;
}

interface ToastCtx {
  push: (message: string, kind?: ToastKind, durationMs?: number) => number;
  success: (msg: string, durationMs?: number) => number;
  error: (msg: string, durationMs?: number) => number;
  info: (msg: string, durationMs?: number) => number;
  warn: (msg: string, durationMs?: number) => number;
  dismiss: (id: number) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, kind: ToastKind = 'info', durationMs = 4500) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, kind, message, durationMs }]);
      if (durationMs > 0) {
        setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), durationMs);
      }
      return id;
    },
    [],
  );

  const api = useMemo<ToastCtx>(
    () => ({
      push,
      success: (m, d) => push(m, 'success', d),
      error: (m, d) => push(m, 'error', d ?? 6000),
      info: (m, d) => push(m, 'info', d),
      warn: (m, d) => push(m, 'warn', d),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </Ctx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (toast.durationMs <= 0) return;
    const t = setTimeout(() => setLeaving(true), Math.max(0, toast.durationMs - 180));
    return () => clearTimeout(t);
  }, [toast.durationMs]);

  const bg = {
    success: 'bg-emerald-50 border-emerald-300 text-emerald-800',
    error: 'bg-red-50 border-red-300 text-red-800',
    info: 'bg-slate-50 border-slate-300 text-slate-800',
    warn: 'bg-amber-50 border-amber-300 text-amber-800',
  }[toast.kind];

  const icon = { success: '✓', error: '✕', info: 'i', warn: '!' }[toast.kind];

  return (
    <div
      className={`pointer-events-auto min-w-[260px] max-w-sm px-4 py-2.5 rounded-md border shadow-sm flex items-start gap-3 text-sm transition-all duration-150 ${bg} ${
        leaving ? 'opacity-0 translate-x-2' : 'opacity-100'
      }`}
    >
      <div className="font-bold w-5 text-center leading-5 shrink-0">{icon}</div>
      <div className="flex-1 break-words">{toast.message}</div>
      <button
        aria-label="Fechar"
        onClick={() => onDismiss(toast.id)}
        className="text-slate-400 hover:text-slate-700 leading-none shrink-0"
      >
        ×
      </button>
    </div>
  );
}
